import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CheckmarkIcon, ShieldCheckIcon, PermGeneralIcon } from '../../assets/icons';
import Breadcrumbs from '../../components/Breadcrumbs';
import { Spinner } from '../../components/ui';
import {
  useGetPermissionsQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useCreateRolePermissionMutation,
  type Permission,
} from '../../features/roles/rolesApi';

const FIELD =
  'w-full rounded-md border bg-[#f8f9ff] px-4 py-2.5 text-sm text-ink placeholder:text-[#9ca3af] transition-colors focus:outline-none focus:ring-2';

interface RoleFormValues {
  name: string;
  description: string;
}

const roleValidationSchema = Yup.object({
  name: Yup.string().trim().required('Role name is required').max(100, 'Name is too long'),
  description: Yup.string().trim().max(500, 'Description is too long'),
});

/** Best-effort label + group for a permission (the API shape may vary). */
const permLabel = (p: Permission) => p.name || p.codename || `Permission ${p.id}`;
const permGroup = (p: Permission) => p.group || p.module || p.category || 'General';

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={[
        'flex size-5 shrink-0 items-center justify-center rounded-[2.5px] transition-colors',
        checked ? 'bg-brand text-white' : 'border border-[#767676] bg-white',
      ].join(' ')}
    >
      {checked && <CheckmarkIcon className="size-4" />}
    </span>
  );
}

export default function CreateRolePage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Permissions already attached to the role (edit mode) — not re-posted on save.
  const [initialSelected, setInitialSelected] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const { data: permissions, isFetching: permsLoading } = useGetPermissionsQuery();
  const { data: role } = useGetRoleQuery(editId!, { skip: !isEdit });
  const [createRole, { isLoading: isCreating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();
  const [attachPermission] = useCreateRolePermissionMutation();
  const saving = isCreating || isUpdating;

  const formik = useFormik<RoleFormValues>({
    initialValues: { name: role?.name ?? '', description: role?.description ?? '' },
    enableReinitialize: true,
    validationSchema: roleValidationSchema,
    onSubmit: (values) => save(values),
  });

  // Edit mode: pre-check attached permissions if the role exposes them.
  useEffect(() => {
    if (!role) return;
    const attached = (role as { permissions?: Array<number | { id: number }> }).permissions;
    if (Array.isArray(attached)) {
      const ids = new Set(attached.map((p) => (typeof p === 'number' ? p : p.id)));
      setSelected(ids);
      setInitialSelected(ids);
    }
  }, [role]);

  const nameError = formik.touched.name && formik.errors.name ? formik.errors.name : undefined;
  const descError =
    formik.touched.description && formik.errors.description ? formik.errors.description : undefined;
  const fieldCls = (invalid?: string) =>
    [
      FIELD,
      invalid
        ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
        : 'border-line focus:border-brand focus:ring-brand/15',
    ].join(' ');

  // Bucket the permissions into groups for the matrix.
  const groups = useMemo(() => {
    const map = new Map<string, Permission[]>();
    (permissions ?? []).forEach((p) => {
      const key = permGroup(p);
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    });
    return Array.from(map, ([name, perms]) => ({ name, perms }));
  }, [permissions]);

  const allIds = useMemo(() => (permissions ?? []).map((p) => p.id), [permissions]);

  const toggle = (permId: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });

  const selectAll = () => setSelected(new Set(allIds));
  const deselectAll = () => setSelected(new Set());

  const save = async (values: RoleFormValues) => {
    setError(null);
    try {
      const patch = { name: values.name.trim(), description: values.description.trim() };
      let roleId: number;
      if (isEdit && editId) {
        const updated = await updateRole({ id: editId, patch }).unwrap();
        roleId = updated?.id ?? Number(editId);
      } else {
        const created = await createRole(patch).unwrap();
        roleId = created.id;
      }
      // Attach every newly-selected permission (skip ones already attached in edit mode).
      const toAttach = Array.from(selected).filter((pid) => !initialSelected.has(pid));
      await Promise.all(
        toAttach.map((permission) => attachPermission({ role: roleId, permission }).unwrap()),
      );
      navigate('/admin/roles');
    } catch (err) {
      const status = (err as { status?: number | string })?.status;
      setError(
        typeof status === 'number'
          ? `Could not save the role (server responded ${status}).`
          : 'Could not save the role. Please try again.',
      );
    }
  };

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6 pb-24">
      {/* Breadcrumb + heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            { label: 'Roles and Permissions', to: '/admin/roles' },
            { label: isEdit ? 'Edit Role' : 'Create New Role' },
          ]}
        />
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">
          {isEdit ? 'Edit Role' : 'Create New Role'}
        </h1>
        <p className="max-w-[672px] text-sm text-ink-secondary">
          Define a functional role and assign specific permissions across system modules to
          maintain strict access control.
        </p>
      </div>

      {/* General information card */}
      <section className="overflow-hidden rounded-lg bg-white shadow-card">
        <div className="flex items-center gap-4 border-b border-line/30 px-6 py-6">
          <span className="flex size-10 items-center justify-center rounded bg-brand/10 text-brand">
            <PermGeneralIcon className="size-5" />
          </span>
          <div className="flex flex-col">
            <h2 className="text-base text-ink-900">General Information</h2>
            <span className="text-[11px] font-medium uppercase tracking-[0.55px] text-ink-secondary">
              Identity &amp; Scope
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-12">
          <div className="flex flex-col gap-1.5 md:col-span-5">
            <label htmlFor="role-name" className="text-base text-ink-900">
              Role Name <span className="text-[#ba1a1a]">*</span>
            </label>
            <input
              id="role-name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="e.g., Regional Manager"
              className={fieldCls(nameError)}
            />
            {nameError ? (
              <span className="text-xs font-medium text-[#ba1a1a]">{nameError}</span>
            ) : (
              <span className="text-xs text-ink-secondary">
                A unique identifier for this functional group.
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-7">
            <label htmlFor="role-desc" className="text-base text-ink-900">
              Role Description
            </label>
            <textarea
              id="role-desc"
              name="description"
              rows={3}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Describe the scope and responsibilities of this role..."
              className={[fieldCls(descError), 'resize-none'].join(' ')}
            />
            {descError && <span className="text-xs font-medium text-[#ba1a1a]">{descError}</span>}
          </div>
        </div>
      </section>

      {/* Permission matrix */}
      <div className="flex flex-wrap items-center gap-4 px-1">
        <span className="text-[13px] font-bold uppercase tracking-[1.3px] text-ink-secondary">
          Permission Matrix
        </span>
        <span className="hidden h-px w-32 bg-line sm:block" />
        <div className="ml-auto flex items-center gap-4 text-[13px] font-semibold tracking-[0.65px]">
          <button type="button" onClick={selectAll} className="text-brand-dark hover:underline">
            Select All
          </button>
          <button type="button" onClick={deselectAll} className="text-ink-secondary hover:underline">
            Deselect All
          </button>
        </div>
      </div>

      {permsLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : groups.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line py-10 text-center text-sm text-ink-secondary">
          No permissions available.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {groups.map((group) => (
            <section key={group.name} className="flex flex-col gap-4 rounded-lg bg-brand-shade p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PermGeneralIcon className="size-5 text-ink-secondary" />
                  <h3 className="text-base capitalize text-ink-900">{group.name}</h3>
                </div>
                <span className="rounded bg-[#d3e4fe] px-2 py-0.5 text-[11px] font-medium text-ink-secondary">
                  {group.perms.length} Perms
                </span>
              </div>

              <div className="flex max-h-[192px] flex-col gap-1 overflow-auto pr-1">
                {group.perms.map((perm) => {
                  const checked = selected.has(perm.id);
                  return (
                    <button
                      key={perm.id}
                      type="button"
                      onClick={() => toggle(perm.id)}
                      className="flex items-center gap-4 rounded p-2 text-left transition-colors hover:bg-white/60"
                    >
                      <Checkbox checked={checked} />
                      <span className="text-sm text-ink-900">{permLabel(perm)}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-[#ba1a1a]">
          {error}
        </p>
      )}

      {/* Footer actions */}
      <div className="flex justify-end pt-2">
        <div className="flex items-center gap-4 rounded-xl border border-[#e9e9e9] bg-white/80 p-2 shadow-card backdrop-blur">
          <button
            type="button"
            onClick={() => navigate('/admin/roles')}
            className="rounded-xl px-8 py-2 text-base text-ink-900 transition-colors hover:bg-brand-shade"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 rounded-xl bg-brand px-8 py-2 text-base text-white shadow-[0px_10px_15px_-3px_rgba(0,74,198,0.3),0px_4px_6px_-4px_rgba(0,74,198,0.3)] transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ShieldCheckIcon className="h-[15px] w-3" />
            {saving ? 'Saving…' : isEdit ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </div>
    </form>
  );
}
